import { useCallback, useEffect, useState } from 'react';

import {
  cacheImage,
  getCachedImageStatus,
  markImageCached,
  markImageErrored,
} from '../utils/imageCache';

export const useCachedImage = (src: string) => {
  const [isLoading, setIsLoading] = useState(() => {
    if (!src) {
      return false;
    }

    return getCachedImageStatus(src) !== 'loaded';
  });

  useEffect(() => {
    if (!src) {
      setIsLoading(false);
      return;
    }

    if (getCachedImageStatus(src) === 'loaded') {
      setIsLoading(false);
      return;
    }

    let isSubscribed = true;
    setIsLoading(true);

    void cacheImage(src)
      .then(() => {
        if (isSubscribed) {
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (isSubscribed) {
          setIsLoading(false);
        }
      });

    return () => {
      isSubscribed = false;
    };
  }, [src]);

  const handleLoad = useCallback(() => {
    markImageCached(src);
    setIsLoading(false);
  }, [src]);

  const handleError = useCallback(() => {
    markImageErrored(src);
    setIsLoading(false);
  }, [src]);

  return {
    isLoading,
    handleLoad,
    handleError,
  };
};
